import { PayloadAction } from '@reduxjs/toolkit';
import {
  call,
  put,
  delay,
  takeLatest,
  CallEffect,
  PutEffect,
  select,
  SelectEffect,
} from 'redux-saga/effects';

import api from 'src/services/graphicsApi';

import { StateType } from '../reducers';
import { NOTIFY } from '../reducers/notification';
import {
  actions,
  REQUEST_CREATE_PLAYLIST_DISPLAY_HUB_STORY,
  REQUEST_DELETE_PLAYLIST_DISPLAY_HUB_STORY,
  REQUEST_DUPLICATE_PLAYLIST_DISPLAY_HUB_STORY,
  REQUEST_GET_ALL_PLAYLIST_DISPLAY_HUB_STORIES,
  REQUEST_REORDER_PLAYLIST_DISPLAY_HUB_STORY,
  REQUEST_SET_SELECTED_PLAYLIST_DISPLAY_HUB_STORY,
  PlaylistDisplayHubStoryResponseType,
  PlaylistDisplayHubStoriesResponseType,
} from '../reducers/playlistDisplayHubStory';
import {
  REQUEST_GET_ALL_PLAYLIST_DISPLAY_HUBS,
  REQUEST_UPDATE_PLAYLIST_DISPLAY_HUB_PREVIEW,
} from '../reducers/playlistDisplayHub';

type PlaylistDisplayHubApiResponse = void | {
  data: PlaylistDisplayHubStoryResponseType;
  status: number;
};
type PlaylistDisplayHubsApiResponse = void | {
  data: PlaylistDisplayHubStoriesResponseType;
  status: number;
};

function* createPlaylistDisplayHubStory(
  action: PayloadAction<{ rundownId: string; playlistDisplayHubId: string; name: string }>
): Generator<CallEffect<PlaylistDisplayHubApiResponse> | PutEffect, void, void> {
  try {
    const { rundownId, playlistDisplayHubId, name } = action.payload;
    const response = yield call(api.post, `/playlistDisplayHubs/${playlistDisplayHubId}/stories`, {
      name,
    });

    const apiResponse = response as PlaylistDisplayHubApiResponse;
    if (apiResponse?.data) {
      yield delay(100);
      yield put({
        type: REQUEST_GET_ALL_PLAYLIST_DISPLAY_HUBS,
        payload: { rundownId },
      });
      yield put({
        type: REQUEST_GET_ALL_PLAYLIST_DISPLAY_HUB_STORIES,
        payload: { playlistDisplayHubId },
      });
    }
  } catch (error) {
    const apiError = error as Error;
    yield put({
      type: NOTIFY,
      payload: {
        variant: 'error',
        title: apiError.name,
        description: apiError.message,
      },
    });
    yield put(
      actions.failedCreatePlaylistDisplayHubStory(
        'Something went wrong. Could not add displayhub to playlist'
      )
    );
  }
}

function* deletePlaylistDisplayHubStory(
  action: PayloadAction<{ playlistDisplayHubId: string; playlistDisplayHubStoryId: string }>
): Generator<CallEffect | PutEffect | SelectEffect> {
  try {
    const { playlistDisplayHubId, playlistDisplayHubStoryId } = action.payload;

    yield call(
      api.delete,
      `/playlistDisplayHubs/${playlistDisplayHubId}/stories/${playlistDisplayHubStoryId}`
    );

    const rundownState = yield select((state: StateType) => state.rundown.detail.data);
    const currentRundown = rundownState as Rundown | null;

    if (currentRundown) {
      yield put({
        type: REQUEST_GET_ALL_PLAYLIST_DISPLAY_HUBS,
        payload: { rundownId: currentRundown.id },
      });
    }

    yield put({
      type: REQUEST_GET_ALL_PLAYLIST_DISPLAY_HUB_STORIES,
      payload: { playlistDisplayHubId },
    });

    yield put({
      type: REQUEST_SET_SELECTED_PLAYLIST_DISPLAY_HUB_STORY,
      payload: { playlistDisplayHubStory: null },
    });

    yield put({
      type: NOTIFY,
      payload: { variant: 'success', title: 'Display Hub Story deleted' },
    });

    yield put(actions.receiveDeletePlaylistDisplayHubStory(playlistDisplayHubId));
  } catch (error) {
    const apiError = error as Error;
    yield put({
      type: NOTIFY,
      payload: {
        variant: 'error',
        title: apiError.name,
        description: apiError.message,
      },
    });
    yield put(actions.failedDeletePlaylistDisplayHubStory(apiError.message));
  }
}

function* duplicatePlaylistDisplayHubStory(
  action: PayloadAction<{ playlistDisplayHubId: string; playlistDisplayHubStoryId: string }>
): Generator<CallEffect | PutEffect, void, PlaylistDisplayHubApiResponse> {
  try {
    const { playlistDisplayHubId, playlistDisplayHubStoryId } = action.payload;
    const response = yield call(
      api.post,
      `/playlistDisplayHubs/${playlistDisplayHubId}/stories/${playlistDisplayHubStoryId}/duplicate`
    );

    if (!response?.data) return;

    const rundown = response.data.playlistDisplayHub?.playlist?.rundown;

    if (rundown) {
      yield put({
        type: REQUEST_GET_ALL_PLAYLIST_DISPLAY_HUBS,
        payload: { rundownId: rundown.id },
      });
    }

    yield put({
      type: REQUEST_GET_ALL_PLAYLIST_DISPLAY_HUB_STORIES,
      payload: { playlistDisplayHubId },
    });

    yield put(actions.receiveDuplicatePlaylistDisplayHubStory());
  } catch (error) {
    const apiError = error as Error;
    yield put({
      type: NOTIFY,
      payload: {
        variant: 'error',
        title: apiError.name,
        description: apiError.message,
      },
    });
    yield put(
      actions.failedDuplicatePlaylistDisplayHubStory(
        'Something went wrong. Could not duplicate displayhub from playlist'
      )
    );
  }
}

function* getRundownPlaylistDisplayHubStories(
  action: PayloadAction<{ playlistDisplayHubId: string }>
): Generator<CallEffect | PutEffect, void, PlaylistDisplayHubsApiResponse> {
  try {
    const { playlistDisplayHubId } = action.payload;
    const response = yield call(api.get, `/playlistDisplayHubs/${playlistDisplayHubId}/stories`);

    if (response?.data) {
      yield put(actions.receiveGetPlaylistDisplayHubStories(response.data));
    }
  } catch (error) {
    const apiError = error as Error;
    yield put({
      type: NOTIFY,
      payload: {
        variant: 'error',
        title: apiError.name,
        description: apiError.message,
      },
    });
    yield put(
      actions.failedGetPlaylistDisplayHubStories(
        'Something went wrong getting playlist displayhubs'
      )
    );
  }
}

function* updateOrderPlaylistDisplayHubStories(
  action: PayloadAction<{ playlistDisplayHubId: string; playlistDisplayHubStoriesIds: string[] }>
): Generator<CallEffect | PutEffect, void, void> {
  try {
    const { playlistDisplayHubId, playlistDisplayHubStoriesIds } = action.payload;
    yield put(actions.receiveReorderPlaylistDisplayHubStories(playlistDisplayHubStoriesIds));
    yield call(api.patch, `/playlistDisplayHubs/${playlistDisplayHubId}/stories/reorder`, {
      playlistDisplayHubStoriesIds,
    });
  } catch (error) {
    const apiError = error as Error;
    yield put({
      type: NOTIFY,
      payload: {
        variant: 'error',
        title: apiError.name,
        description: apiError.message,
      },
    });
    yield put(
      actions.failedReorderPlaylistDisplayHubStories(
        'Something went wrong. Could not reorder displayhubs'
      )
    );
  }
}

function* setSelectedPlaylistDisplayHubStory(
  action: PayloadAction<{ playlistDisplayHubStory: PlaylistDisplayHubStory | null }>
): Generator<CallEffect | PutEffect | SelectEffect> {
  try {
    const storyState = yield select(
      (state) => state.playlistDisplayHubStory.selectedPlaylistDisplayHubStory
    );

    const selectedPlaylistDisplayHubStory = storyState as PlaylistDisplayHubStory | null;

    const { playlistDisplayHubStory } = action.payload;
    yield put(actions.receiveSetSelectedPlaylistDisplayHubStory(playlistDisplayHubStory));

    if (selectedPlaylistDisplayHubStory?.id === playlistDisplayHubStory?.id) return;

    if (!playlistDisplayHubStory) {
      const totalGraphics = selectedPlaylistDisplayHubStory?.playlistGraphicIds.length;
      if (!totalGraphics) return;

      const images = Object.fromEntries(
        Array.from({ length: totalGraphics }, (_, i) => [`image${i + 1}`, ''])
      );

      yield put({ type: REQUEST_UPDATE_PLAYLIST_DISPLAY_HUB_PREVIEW, payload: { images } });
      return;
    }

    const graphicState = yield select(
      (state: StateType) => state.playlistGraphic.displayHubGraphics
    );

    const allDisplayHubGraphics = graphicState as PlaylistGraphic[] | null;

    const storyGraphics = allDisplayHubGraphics?.filter((playlistGraphic) =>
      playlistDisplayHubStory.playlistGraphicIds.includes(playlistGraphic.id)
    );

    const images = storyGraphics?.reduce<Record<string, string>>((acc, graphic, i) => {
      acc[`image${i + 1}`] = graphic.propertiesValues.image;
      return acc;
    }, {});

    if (!images) return;

    yield put({ type: REQUEST_UPDATE_PLAYLIST_DISPLAY_HUB_PREVIEW, payload: { images } });
  } catch (error) {
    const apiError = error as Error;
    yield put({
      type: NOTIFY,
      payload: {
        variant: 'error',
        title: apiError.name,
        description: apiError.message,
      },
    });
    yield put(
      actions.failedSetSelectedPlaylistDisplayHubStory(
        'Something went wrong. Could not set displayhub'
      )
    );
  }
}

export default function* root() {
  yield takeLatest(REQUEST_CREATE_PLAYLIST_DISPLAY_HUB_STORY, createPlaylistDisplayHubStory);
  yield takeLatest(REQUEST_DELETE_PLAYLIST_DISPLAY_HUB_STORY, deletePlaylistDisplayHubStory);
  yield takeLatest(REQUEST_DUPLICATE_PLAYLIST_DISPLAY_HUB_STORY, duplicatePlaylistDisplayHubStory);
  yield takeLatest(
    REQUEST_REORDER_PLAYLIST_DISPLAY_HUB_STORY,
    updateOrderPlaylistDisplayHubStories
  );
  yield takeLatest(
    REQUEST_GET_ALL_PLAYLIST_DISPLAY_HUB_STORIES,
    getRundownPlaylistDisplayHubStories
  );
  yield takeLatest(
    REQUEST_SET_SELECTED_PLAYLIST_DISPLAY_HUB_STORY,
    setSelectedPlaylistDisplayHubStory
  );
}
